import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ── Types ────────────────────────────────────────────────────────────────

export type InventoryLocation = {
  id: string;
  org_id: string;
  name: string;
  address: string | null;
  notes: string | null;
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type PartLocationStock = {
  location_id: string;
  location_name: string;
  parent_location_name: string | null;
  quantity: number;
};

// ── Query key ─────────────────────────────────────────────────────────────

const LOCATIONS_KEY = ["inventory_locations"] as const;
const STOCK_KEY = (partId: string) => ["part_location_stock", partId] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────

/** All active locations for the org. */
export function useInventoryLocations() {
  return useQuery({
    queryKey: LOCATIONS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inventory_location")
        .select("*")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data as InventoryLocation[];
    },
  });
}

/** Top-level locations (no parent). */
export function useTopLevelLocations() {
  const { data = [] } = useInventoryLocations();
  return data.filter((l) => l.parent_id === null);
}

/** Direct children of a given location. */
export function useSubLocations(parentId: string | null) {
  const { data = [] } = useInventoryLocations();
  if (!parentId) return [];
  return data.filter((l) => l.parent_id === parentId);
}

/** Stock breakdown for a specific part across all locations. */
export function usePartLocationStock(partId: string): PartLocationStock[] {
  const { data } = useQuery({
    queryKey: STOCK_KEY(partId),
    queryFn: async () => fetchPartLocationStock(partId),
    enabled: !!partId,
  });
  return data ?? [];
}

// ── Mutations ─────────────────────────────────────────────────────────────

export type UpsertStockEntry = {
  part_id: string;
  location_id: string;
  quantity: number;
  org_id: string;
};

export function useUpsertPartLocationStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entry: UpsertStockEntry) => {
      const { error } = await supabase
        .from("part_location_stock")
        .upsert(
          { ...entry },
          { onConflict: "part_id,location_id" }
        );
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: STOCK_KEY(vars.part_id) });
    },
  });
}

export function useDeletePartLocationStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partId, locationId }: { partId: string; locationId: string }) => {
      const { error } = await supabase
        .from("part_location_stock")
        .delete()
        .eq("part_id", partId)
        .eq("location_id", locationId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: STOCK_KEY(vars.partId) });
    },
  });
}

export function useCreateInventoryLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (loc: { name: string; parent_id?: string | null; address?: string | null; notes?: string | null; sort_order?: number }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get org_id from user_account
      const { data: account, error: accErr } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", user.id)
        .single();
      if (accErr) throw accErr;

      const { error } = await supabase
        .from("inventory_location")
        .insert({ ...loc, org_id: account.org_id });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

export function useUpdateInventoryLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<InventoryLocation> & { id: string }) => {
      const { error } = await supabase
        .from("inventory_location")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

export function useDeleteInventoryLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("inventory_location")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LOCATIONS_KEY }),
  });
}

// ── Plain (non-hook) helpers ──────────────────────────────────────────────

/** Fetches part location stock — used by export handlers outside React render. */
async function fetchPartLocationStock(partId: string): Promise<PartLocationStock[]> {
  // Join part_location_stock → inventory_location → parent location
  const { data, error } = await supabase
    .from("part_location_stock")
    .select(`
      location_id,
      quantity,
      inventory_location!inner (
        name,
        parent_id,
        parent:inventory_location!parent_id ( name )
      )
    `)
    .eq("part_id", partId);

  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    location_id: row.location_id,
    location_name: row.inventory_location.name,
    parent_location_name: row.inventory_location.parent?.name ?? null,
    quantity: row.quantity,
  }));
}

/** Cached location stock for all parts — populated once per export session. */
let _stockCache: Map<string, PartLocationStock[]> | null = null;

/** Non-hook version for use in export callbacks. Reads from in-memory cache. */
export function getPartLocationStock(partId: string): PartLocationStock[] {
  return _stockCache?.get(partId) ?? [];
}

/** Call this before triggering an export to warm the stock cache. */
export async function loadAllPartLocationStock(partIds: string[]): Promise<void> {
  if (partIds.length === 0) return;

  const { data, error } = await supabase
    .from("part_location_stock")
    .select(`
      part_id,
      location_id,
      quantity,
      inventory_location!inner (
        name,
        parent_id,
        parent:inventory_location!parent_id ( name )
      )
    `)
    .in("part_id", partIds);

  if (error) throw error;

  _stockCache = new Map();
  for (const row of data ?? []) {
    const entry: PartLocationStock = {
      location_id: row.location_id,
      location_name: (row as any).inventory_location.name,
      parent_location_name: (row as any).inventory_location.parent?.name ?? null,
      quantity: row.quantity,
    };
    const list = _stockCache.get(row.part_id) ?? [];
    list.push(entry);
    _stockCache.set(row.part_id, list);
  }
}
